import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import html2canvas from 'html2canvas';

import Button from 'components/Button';
import Icon from 'components/Icon';

import theme from 'styles/theme';

import { Feedback, NotificationsDot } from './Header.styled';

const Help = ({
  isOpen,
  helpSubmit,
  feedbackSending,
  feedbackSent,
  toggleSubmitFeedback,
  showWhatsNew,
  setShowWhatsNew,
  showKeyboardShortcuts,
  setShowKeyboardShortcuts,
}) => {
  const [isActive, setIsActive] = useState(false);

  const [bugInput, setBugInput] = useState('');
  const [screenshotMode, setScreenshotMode] = useState(false);
  const [selection, setSelection] = useState({
    top: 0,
    left: 0,
    height: 0,
    width: 0,
  });
  const [capturing, setCapturing] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [sent, setSent] = useState(false);

  const toggleActive = useCallback(() => {
    setIsActive((state) => !state);
  }, []);

  const toggleDropdown = useCallback(
    (e) => {
      if (!e.target.href || !e.target.href.match(/ruttl.com/))
        toggleSubmitFeedback(!isOpen);

      setIsActive(false);
    },
    [isOpen, toggleSubmitFeedback],
  );

  const toggleWhatsNew = useCallback(() => {
    setShowWhatsNew((state) => !state);

    setIsActive(false);
  }, [setShowWhatsNew]);

  const toggleKeyboardShortcuts = useCallback(() => {
    setShowKeyboardShortcuts(!showKeyboardShortcuts);

    setIsActive(false);
  }, [setShowKeyboardShortcuts, showKeyboardShortcuts]);

  const bugChangeHandler = useCallback((e) => {
    setBugInput(e.target.value);
  }, []);

  const toggleScreenshotMode = useCallback(() => {
    setScreenshotMode((state) => !state);
  }, []);

  const screenshotMouseMove = useCallback(
    (e) => {
      e.persist();
      if (capturing)
        setSelection((state) => ({
          ...state,
          width: Math.abs(state.left - e.clientX),
          height: Math.abs(state.top - e.clientY),
        }));
    },
    [capturing],
  );

  const screenshotMouseUp = useCallback(() => {
    const newSelection = { ...selection };
    const frame = document.getElementById('ruttl-web-frame');

    setSelection({ top: 0, left: 0, width: 0, height: 0 });
    setCapturing(false);
    toggleScreenshotMode();
    setTimeout(() => {
      if (frame)
        html2canvas(frame.contentDocument.body, {
          allowTaint: true,
          proxy: `/proxy/?url=`,
          useCORS: true,
          height: frame.clientHeight,
          width: frame.clientWidth,
          backgroundColor:
            frame.contentWindow.getComputedStyle(frame.contentDocument.body)
              .backgroundColor || null,
          logging: false,
        }).then((frameCanvas) => {
          const isMobile = frame.clientHeight === 731;
          const frameImage = document.createElement('img');

          frameImage.src = frameCanvas.toDataURL();
          frameImage.className = `iframe-screenshot${
            isMobile ? ' is-mobile' : ''
          }`;
          frame.parentElement.appendChild(frameImage);
          frame.style.display = 'none';

          html2canvas(document.body, {
            allowTaint: true,
            proxy: `/proxy/?url=`,
            useCORS: true,
            x: newSelection.left + 2,
            y: newSelection.top + 2,
            height: newSelection.height - 4,
            width: newSelection.width - 4,
            backgroundColor:
              window.getComputedStyle(document.body).backgroundColor || null,
            logging: false,
            ignoreElements: (element) =>
              element.closest('#feedback-modal') !== null,
          }).then((canvas) => {
            frame.style.display = null;
            frameImage.remove();
            setScreenshots((state) => [...state, canvas.toDataURL()]);
          });
        });
      else
        html2canvas(document.body, {
          allowTaint: true,
          proxy: `/proxy/?url=`,
          useCORS: true,
          x: newSelection.left + 2,
          y: newSelection.top + 2,
          height: newSelection.height - 4,
          width: newSelection.width - 4,
          backgroundColor:
            window.getComputedStyle(document.body).backgroundColor || null,
          logging: false,
          ignoreElements: (element) =>
            element.closest('#feedback-modal') !== null,
        }).then((canvas) => {
          setScreenshots((state) => [...state, canvas.toDataURL()]);
        });
    }, 100);
  }, [selection, toggleScreenshotMode]);

  const screenshotMouseDown = useCallback((e) => {
    setCapturing(true);
    setSelection({
      top: e.clientY,
      left: e.clientX,
      height: 0,
      width: 0,
    });
  }, []);

  const deleteScreenshot = useCallback((e) => {
    const { id } = e.target.closest('.message-image').dataset;

    setScreenshots((state) => state.filter((i, index) => index !== Number(id)));
  }, []);

  const submitHandler = useCallback(() => {
    helpSubmit({ message: bugInput, screenshots });
  }, [bugInput, helpSubmit, screenshots]);

  const toggleSent = useCallback(() => {
    setSent(!sent);
    setBugInput('');
    setSelection({
      top: 0,
      left: 0,
      height: 0,
      width: 0,
    });
    setScreenshots([]);
  }, [sent]);

  useEffect(() => {
    if (feedbackSent) setSent(true);
  }, [feedbackSent]);

  return (
    <Feedback>
      <Button
        type="button"
        id="dropdown-button"
        className="dropdown-button tooltip-container"
        onClick={toggleActive}>
        <Icon name="help" verticalAlign="middle" height="26px" width="26px" />
        {!isOpen &&
          (bugInput.trim().length > 0 || screenshots.length > 0) &&
          !sent && <NotificationsDot style={{ left: 22, top: 8 }} />}
        <span className="tooltip" style={{ top: -8, left: 15 }}>
          Need help?
        </span>
        <span
          style={{
            position: 'absolute',
            top: 0,
            left: 20,
            color: theme.colors.red,
            fontSize: 18,
          }}>
          &bull;
        </span>
      </Button>
      {isActive && (
        <Button
          type="button"
          onClick={toggleActive}
          className="popup-wrapper absolute-inset"
        />
      )}
      {showWhatsNew && (
        <Button
          type="button"
          onClick={toggleWhatsNew}
          className="whatsnew-overlay"
        />
      )}
      {showKeyboardShortcuts && (
        <Button
          type="button"
          onClick={toggleKeyboardShortcuts}
          className="keyboard-shortcuts-overlay"
        />
      )}
      <ul className={`dropdown-menu align-left${isActive ? ' active' : ''}`}>
        <li>
          <Button type="button" unstyled onClick={toggleDropdown}>
            Send Feedback
          </Button>
        </li>
        <li>
          <a
            href="https://ruttl.com/support/"
            target="_blank"
            rel="noopener noreferrer"
            className="link"
            onClick={toggleDropdown}>
            Support docs
          </a>
        </li>
        <li>
          <Button type="button" unstyled onClick={toggleWhatsNew}>
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            What's New&nbsp;
            <span style={{ color: theme.colors.red, fontSize: 18 }}>
              &bull;
            </span>
          </Button>
        </li>
        <li>
          <Button type="button" unstyled onClick={toggleKeyboardShortcuts}>
            Keyboard Shortcuts
          </Button>
        </li>
      </ul>
      {!screenshotMode && (
        <Button
          type="button"
          onClick={toggleDropdown}
          className="popup-wrapper absolute-inset"
          style={{ display: isOpen ? 'block' : 'none' }}
        />
      )}
      {screenshotMode && (
        <Button
          type="button"
          onMouseDown={screenshotMouseDown}
          onMouseUp={screenshotMouseUp}
          onMouseMove={screenshotMouseMove}
          className="screenshot-wrapper absolute-inset"
          style={capturing ? { backgroundColor: 'transparent' } : {}}>
          <span className="selection" style={selection} />
          {!capturing && (
            <span className="message">
              Click &amp; drag to take a screenshot
            </span>
          )}
        </Button>
      )}
      <div
        id="feedback-modal"
        className={`wrapper${isOpen && !screenshotMode ? ' is-active' : ''}`}>
        <Button type="button" className="close-button">
          <Icon
            name="close"
            height="24px"
            width="18px"
            verticalAlign="middle"
            fill="#ffffff"
            onClick={toggleDropdown}
          />
        </Button>
        <div className="header">
          <span>Feedback</span>
        </div>
        {sent ? (
          <div className="sent center">
            <p>Thanks for your feedback. We&apos;ll work on it.</p>
            <Button type="button" styled fluid onClick={toggleSent}>
              Send another feedback
            </Button>
          </div>
        ) : (
          <>
            <div className="body users-form">
              <textarea
                name="bug-message"
                id="bug-message"
                value={bugInput}
                onChange={bugChangeHandler}
                placeholder="Write your feedback"
                disabled={feedbackSending}
              />
              {screenshots.map((i, index) => (
                <div
                  key={i.substring(20, 40)}
                  data-id={index}
                  className="message-image">
                  <img src={i} alt="screenshot" />
                  <Button
                    type="button"
                    onClick={deleteScreenshot}
                    disabled={feedbackSending}
                    title="Remove screenshot">
                    <Icon name="bin" fill="#ffffff" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                className="screenshot-button"
                disabled={feedbackSending}
                onClick={toggleScreenshotMode}>
                <Icon name="snapshot" verticalAlign="bottom" />
                {screenshots.length > 0
                  ? 'Take another screenshot'
                  : 'Take a screenshot'}
              </Button>
            </div>
            <Button
              type="button"
              className="submit-button"
              styled
              fluid
              disabled={feedbackSending || !bugInput.trim().length}
              onClick={submitHandler}>
              {feedbackSending ? 'Sending...' : 'Submit your feedback'}
            </Button>
          </>
        )}
      </div>
    </Feedback>
  );
};

Help.propTypes = {
  helpSubmit: PropTypes.func.isRequired,
  toggleSubmitFeedback: PropTypes.func.isRequired,
  feedbackSending: PropTypes.bool.isRequired,
  feedbackSent: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  showWhatsNew: PropTypes.bool.isRequired,
  setShowWhatsNew: PropTypes.func.isRequired,
  showKeyboardShortcuts: PropTypes.bool.isRequired,
  setShowKeyboardShortcuts: PropTypes.func.isRequired,
};

export default Help;
